import React from 'react';
import { BarChart, Trash2, Heart, User } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных профилей пульса
 */
const ComparisonSidebar = ({ 
  savedProfiles, 
  deleteSavedProfile, 
  loadSavedProfile 
}) => {
  // Если нет сохраненных профилей
  if (!savedProfiles || savedProfiles.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-red-500" />
          Сравнение профилей
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные профили пульса для сравнения.
        </p>
        <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <Heart className="w-5 h-5 text-red-400 dark:text-red-500 mr-2" />
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Рассчитайте ваш максимальный пульс и сохраните результат, чтобы сравнивать разные профили.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Функция для получения разницы в максимальном пульсе
  const getPulseDifference = (profile, index) => {
    if (index === 0 || !savedProfiles[0]) return null;
    
    const firstProfileMHR = savedProfiles[0].results.maxHeartRate;
    const currentMHR = profile.results.maxHeartRate;
    const difference = currentMHR - firstProfileMHR;
    
    return {
      value: Math.abs(difference),
      isHigher: difference > 0
    };
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-red-500" />
          Сравнение профилей
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedProfiles.length} {pluralize(savedProfiles.length, 'профиль', 'профиля', 'профилей')}
        </span>
      </div>

      <div className="space-y-3">
        {savedProfiles.map((profile, index) => {
          const pulseDifference = getPulseDifference(profile, index);
          
          return (
            <div 
              key={profile.id} 
              className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center">
                  <Heart className={`w-4 h-4 mr-1.5 ${index === 0 ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`} />
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    {profile.name}
                    {index === 0 && (
                      <span className="ml-1.5 text-xs px-1.5 py-0.5 bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 rounded-full">
                        Основной
                      </span>
                    )}
                  </h4>
                </div>
                <div className="flex items-center space-x-1">
                  <Button
                    size="xs"
                    variant="ghost"
                    icon={<Heart className="w-3.5 h-3.5" />}
                    onClick={() => loadSavedProfile(profile.id)}
                    title="Загрузить профиль"
                  />
                  <Button
                    size="xs"
                    variant="ghost"
                    icon={<Trash2 className="w-3.5 h-3.5 text-red-500" />}
                    onClick={() => deleteSavedProfile(profile.id)}
                    title="Удалить"
                  />
                </div>
              </div>
              
              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <div className="flex justify-between">
                  <span className="flex items-center text-xs">
                    <User className="w-3.5 h-3.5 mr-1" />
                    {profile.formData.gender === 'male' ? 'Мужчина' : 'Женщина'}, {profile.formData.age} лет
                  </span>
                  <span className="text-xs">
                    {new Date(profile.date).toLocaleDateString('ru-RU')}
                  </span>
                </div>
                
                <div className="flex items-center justify-between mt-1">
                  <span className="font-medium text-red-600 dark:text-red-400 flex items-center">
                    <Heart className="w-4 h-4 mr-1" />
                    {profile.results.maxHeartRate} уд/мин
                  </span>
                  
                  {pulseDifference && (
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      pulseDifference.isHigher 
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                    }`}>
                      {pulseDifference.isHigher ? '+' : '-'}{pulseDifference.value} уд/мин
                    </span>
                  )}
                </div>
                
                <div className="text-xs mt-1">
                  <span className="text-gray-500 dark:text-gray-500">Формула: </span>
                  {profile.results.formulaName}
                </div>

                {profile.results.restingHeartRate && (
                  <div className="text-xs">
                    <span className="text-gray-500 dark:text-gray-500">Пульс в покое: </span>
                    {profile.results.restingHeartRate} уд/мин
                  </div>
                )}
              </div>
              
              {/* Визуализация зон пульса */}
              <div className="mt-2 relative h-2.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                {profile.results.zones && profile.results.zones.map((zone, zoneIndex) => {
                  // Расчет ширины зоны в процентах
                  const width = ((zone.max - zone.min) / profile.results.maxHeartRate) * 100;
                  const offset = (zone.min / profile.results.maxHeartRate) * 100;
                  
                  // Цвета для зон
                  const zoneColors = [
                    'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-orange-500', 'bg-red-500'
                  ];
                  
                  return (
                    <div 
                      key={zoneIndex}
                      className={`absolute h-full ${zoneColors[zoneIndex]}`}
                      style={{ 
                        left: `${offset}%`, 
                        width: `${width}%`
                      }}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      
      {savedProfiles.length > 0 && (
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p className="text-xs text-blue-700 dark:text-blue-300">
              Первый сохраненный профиль считается основным и используется для сравнения. Для расчета пульса с другими параметрами создайте новый профиль.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, few, many) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return many;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return few;
  }
  return many;
};

export default ComparisonSidebar;